import axios from "axios";
import type { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";

// ── Shared response types (re-exported for consumers) ─────────────────────────

export interface ApiError {
  status?: number;
  message: string;
  raw?: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ApiResponse<T = any> {
  data: T | null;
  error: ApiError | null;
}

// ── Call options ──────────────────────────────────────────────────────────────

export interface CallOptions {
  data?: unknown;
  params?: Record<string, unknown> | null;
  headers?: Record<string, string>;
  signal?: AbortSignal | null;
  responseType?: string;
}

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

// ── Internal helpers ──────────────────────────────────────────────────────────

const isLoginRequest = (url = ""): boolean => {
  const normalizedUrl = String(url).toLowerCase();
  return (
    normalizedUrl.includes("/auth/login") ||
    normalizedUrl.includes("/user/accept-invite")
  );
};

const isAwsUrl = (url = ""): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.hostname.includes("amazonaws.com");
  } catch {
    return false;
  }
};

// ── APIClient class ───────────────────────────────────────────────────────────

class APIClient {
  private _axios: AxiosInstance | null = null;
  private _onUnauthorized: (() => void) | null = null;

  /**
   * @param baseURL  - Base URL for all requests
   * @param debug    - Log initialization info to console
   * @param onUnauthorized - Called on 401 responses (outside login)
   * @param role     - Injected as x-role header
   */
  initializeApiClient(
    baseURL: string,
    debug: boolean,
    onUnauthorized: () => void,
    role: string,
  ): void {
    this._onUnauthorized = onUnauthorized;

    this._axios = axios.create({
      baseURL,
      timeout: 15000,
      headers: { "Content-Type": "application/json" },
    });

    this._axios.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (isAwsUrl(config.url)) {
          config.baseURL = "";
          const headers = config.headers as Record<string, unknown>;
          delete headers["Authorization"];
          delete headers["x-role"];
          return config;
        }

        const currentToken = localStorage.getItem("token");
        const currentRole  = localStorage.getItem("role");

        if (!config.headers["Authorization"] && currentToken) {
          config.headers["Authorization"] = `Bearer ${currentToken}`;
        }
        if (currentRole) {
          config.headers["x-role"] = currentRole;
        }

        return config;
      },
      (error: unknown) => Promise.reject(error),
    );

    this._axios.interceptors.response.use(
      (response) => response.data,
      (error: AxiosError<{ message?: string; fields?: Record<string, string> }>) => {
        const status     = error.response?.status;
        const requestUrl = (error.config?.url as string | undefined) ?? "";
        const message    =
          error.response?.data?.message ?? error.message ?? "An error occurred";

        if (status === 401 && this._onUnauthorized && !isLoginRequest(requestUrl)) {
          this._onUnauthorized();
        }

        return Promise.reject({ status, message, raw: error.response?.data });
      },
    );

    if (debug) {
      console.info("[APIClient] initialized ->", baseURL, "| role:", role);
    }
  }

  static setApiToken(token: string): void {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
  }

  static setRole(role: string): void {
    if (role) {
      localStorage.setItem("role", role);
    } else {
      localStorage.removeItem("role");
    }
  }

  async call<T = any>(
    method: HttpMethod | string,
    url: string,
    {
      data       = null,
      params     = null,
      headers    = {},
      signal     = null,
      responseType,
    }: CallOptions = {},
  ): Promise<ApiResponse<T>> {
    try {
      const config: Record<string, unknown> = { method, url, params, headers };
      if (data        !== null)      config["data"]         = data;
      if (signal      !== null)      config["signal"]       = signal;
      if (responseType !== undefined) config["responseType"] = responseType;

      const result = await this._axios!(config as unknown as AxiosRequestConfig);
      return { data: result as T, error: null };
    } catch (err: unknown) {
      if (
        axios.isCancel(err) ||
        (err as { code?: string })?.code === "ERR_CANCELED"
      ) {
        return { data: null, error: null };
      }
      return { data: null, error: err as ApiError };
    }
  }

  get = <T = any>(
    url: string,
    params?: Record<string, unknown> | null,
    headers?: Record<string, string>,
    signal?: AbortSignal | null,
  ): Promise<ApiResponse<T>> =>
    this.call<T>("GET", url, { params, headers, signal });

  post = <T = any>(
    url: string,
    data?: unknown,
    headers?: Record<string, string>,
  ): Promise<ApiResponse<T>> =>
    this.call<T>("POST", url, { data, headers });

  put = <T = any>(
    url: string,
    data?: unknown,
    headers?: Record<string, string>,
  ): Promise<ApiResponse<T>> =>
    this.call<T>("PUT", url, { data, headers });

  patch = <T = any>(
    url: string,
    data?: unknown,
    headers?: Record<string, string>,
  ): Promise<ApiResponse<T>> =>
    this.call<T>("PATCH", url, { data, headers });

  delete = <T = any>(
    url: string,
    data?: unknown,
    headers?: Record<string, string>,
  ): Promise<ApiResponse<T>> =>
    this.call<T>("DELETE", url, { data, headers });
}

// ── Singleton + initializer ───────────────────────────────────────────────────

const apiClient = new APIClient();

let currentRole = localStorage.getItem("role") ?? "";

export const initializeApiClient = (role: string): void => {
  currentRole = role;
  apiClient.initializeApiClient(
    process.env.REACT_APP_API_BASE_URL ?? "",
    false,
    () => {
      APIClient.setApiToken("");
      APIClient.setRole("");
      localStorage.clear();
      if (window.location.pathname !== "/") {
        window.location.href = "/";
      }
    },
    role,
  );
};

export { APIClient };

initializeApiClient(currentRole);

export default apiClient;
